import type { NativeRecording } from '@muzly/shared';

import type { PcmTake } from './pcmRecorder';

/**
 * takeExport — 녹음 한 테이크를 **내보낼 두 가지 모양**으로 바꾸는 순수 변환.
 *
 * - `encodeWav` — 16-bit PCM WAV(mono). 사람이 듣고 다른 도구로 여는 쪽.
 * - `toTakeJson` — 표본을 base64로 담은 JSON. 기계가 다시 읽는 쪽.
 *
 * **왜 Float32를 JSON 숫자 배열로 안 쓰나**: 44.1kHz 20초면 882,000개다. 숫자 배열로
 * 찍으면 30MB를 넘고 파싱도 느리다. 16-bit로 양자화해 base64로 담으면 ~1.2MB이고,
 * 그 정밀도는 WAV가 담는 것과 똑같다 — 즉 JSON이 WAV보다 정보를 덜 담지 않는다.
 *
 * **이 경로에는** 받을 서버가 없다 — `RECORD_ENDPOINT`(landing 의 `/analyze` 정밀 지표)가
 * 빈 값이다. 진단 회차 자체는 서버로 간다(제출 + 테이크 업로드, ADR-0007); 여기서 만드는
 * 것은 그것과 별개인 **분석용 덤프**다. 그래서 "데이터가 나온다"는 곧 **파일로
 * 내려받는다**는 뜻이고, 그 배선은 admin의 training 저장(`features/training/storage.ts`)과
 * 같은 방식이다. 이 파일은 Blob까지만 만들고 내려받기는 호출자가 한다.
 *
 * 브라우저 API·React 의존 없음 — vitest 대상.
 */

/** Float32(-1..1) → 16-bit 정수. 범위를 넘는 표본은 자른다(클리핑). */
export const toInt16 = (samples: Float32Array): Int16Array => {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    // 음수 쪽이 한 칸 더 넓다(-32768..32767) — 두 방향에 같은 배수를 쓰면 +1.0이 넘친다.
    out[i] = Math.round(clamped * (clamped < 0 ? 0x8000 : 0x7fff));
  }
  return out;
};

const ascii = (view: DataView, at: number, text: string): void => {
  for (let i = 0; i < text.length; i += 1) {
    view.setUint8(at + i, text.charCodeAt(i));
  }
};

/**
 * 16-bit mono PCM WAV의 **바이트열**. 헤더 44바이트 + 표본.
 *
 * `Blob`이 아니라 `ArrayBuffer`를 내는 자리를 따로 둔 이유: Blob은 환경마다 읽는 법이
 * 다르고(jsdom의 Blob에는 `arrayBuffer()`가 없다) 테스트가 헤더 한 바이트도 못 본다.
 * 포장은 `encodeWav`가 한 줄로 한다.
 *
 * 표본 수가 0이어도 유효한 WAV를 낸다 — 빈 파일을 내려받는 편이 "안 나왔다"를 조용히
 * 삼키는 것보다 낫다(길이 0이 곧 증거다).
 */
export const wavBuffer = (take: PcmTake): ArrayBuffer => {
  const pcm = toInt16(take.samples);
  const bytes = pcm.length * 2;
  const buffer = new ArrayBuffer(44 + bytes);
  const view = new DataView(buffer);

  ascii(view, 0, 'RIFF');
  view.setUint32(4, 36 + bytes, true);
  ascii(view, 8, 'WAVE');
  ascii(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // PCM fmt 청크 길이
  view.setUint16(20, 1, true); // 1 = PCM(비압축)
  view.setUint16(22, 1, true); // 채널 1(mono)
  view.setUint32(24, take.sampleRate, true);
  view.setUint32(28, take.sampleRate * 2, true); // byte rate = rate × block align
  view.setUint16(32, 2, true); // block align = 채널 × 2바이트
  view.setUint16(34, 16, true); // bits per sample
  ascii(view, 36, 'data');
  view.setUint32(40, bytes, true);

  for (let i = 0; i < pcm.length; i += 1) {
    view.setInt16(44 + i * 2, pcm[i], true);
  }
  return buffer;
};

export const encodeWav = (take: PcmTake): Blob =>
  new Blob([wavBuffer(take)], { type: 'audio/wav' });

/** 바이트열 → base64. 한 번에 `String.fromCharCode(...)`를 부르면 스택이 넘친다. */
const toBase64 = (bytes: Uint8Array): string => {
  const CHUNK = 0x8000;
  let binary = '';
  for (let at = 0; at < bytes.length; at += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK));
  }
  return btoa(binary);
};

export interface TakeJson {
  /** 이 파일 모양의 버전. 읽는 쪽이 모양이 바뀐 걸 알아챌 수 있게. */
  schema: 'muzly.take/1';
  /** 어느 진단의 어느 세그먼트인가(`sustain` | `low` | `high` | `song`). */
  segment: string;
  /** 이 테이크를 끝낸 시각(epoch ms). */
  recordedAt: number;
  pcm: {
    /** 언제나 `pcm_s16le`. WAV의 data 청크와 같은 바이트다. */
    encoding: 'pcm_s16le';
    channels: 1;
    /** **실제로 열린** 레이트. 요청값과 다를 수 있다. */
    sampleRate: number;
    /** 요청했던 레이트 — 실제와 다르면 기기가 요청을 거부한 것이다. */
    requestedSampleRate: number;
    sampleCount: number;
    durationSec: number;
    /** 보관 창을 넘겨 앞부분이 잘렸나. true면 이 데이터는 **끝의 N초**다. */
    trimmed: boolean;
    /** 리틀엔디언 16-bit 표본을 base64로. **네이티브 회차에는 없다** — `uri`를 본다. */
    base64?: string;
    /** 기기 샌드박스의 원음 경로. **브라우저 회차에는 없다** — `base64`를 본다. */
    uri?: string;
    /** 캡처 불연속 횟수(네이티브 회차만). 0이 아니면 오디오에 구멍이 있다. */
    gapCount?: number;
  };
  /** 엔진이 실제로 본 f0 프레임 — PCM과 같은 테이크의 분석 결과다. */
  frames: TakeFrame[];
}

/**
 * 내보내는 프레임. 네이티브 프레임 전체를 그대로 싣지 않는다 — 이 파일의 목적은
 * "이 소리에서 무엇을 읽었나"를 되짚는 것이고, 그 답에 필요한 축만 남긴다.
 */
export interface TakeFrame {
  t: number;
  f0Hz: number | null;
  midi: number | null;
  confidence: number;
  rmsDb: number;
  voiced: boolean;
}

/**
 * 네이티브 회차의 manifest — **표본이 아니라 파일을 가리킨다**.
 *
 * `TakeJson`과 같은 스키마를 쓰되 `pcm.base64`가 없고 `pcm.uri`가 있다. 두 모양을 한
 * 타입으로 두는 이유: 받는 쪽이 "이건 진단 테이크 파일"이라는 사실을 먼저 읽고, 그
 * 다음에 원음이 인라인인지 기기에 있는지를 본다. 스키마를 갈라 놓으면 소비처가 두 벌이
 * 된다.
 */
export const toNativeTakeJson = (input: {
  segment: string;
  recording: NativeRecording;
  frames: TakeFrame[];
  recordedAt: number;
}): TakeJson => ({
  schema: 'muzly.take/1',
  segment: input.segment,
  recordedAt: input.recordedAt,
  pcm: {
    encoding: 'pcm_s16le',
    channels: 1,
    sampleRate: input.recording.sampleRate,
    // 네이티브는 캡처 레이트 그대로 쓴다(리샘플 없음 — GOAL.md) → 요청과 실제가 같다.
    requestedSampleRate: input.recording.sampleRate,
    sampleCount: input.recording.sampleCount,
    durationSec: input.recording.durationSec,
    // 기기 파일은 창으로 자르지 않는다 — 세션 전체가 그대로 있다.
    trimmed: false,
    /**
     * 기기 샌드박스 경로. 진단 테이크는 업로드가 성공하면 **이 파일이 지워진다**
     * (ADR-0007) — 그때는 이 경로가 가리키는 것이 없고, 그 회차 소리는 서버 미디어에 있다.
     */
    uri: input.recording.uri,
    /**
     * 캡처 불연속 횟수. 0이 아니면 **이 오디오에는 구멍이 있다** — 떨어진 표본은 그냥
     * 없어서 파일이 벽시계보다 짧고 프레임 타임스탬프와 1:1로 안 맞는다.
     */
    gapCount: input.recording.gapCount,
  },
  frames: input.frames,
});

export const toTakeJson = (input: {
  segment: string;
  take: PcmTake;
  requestedSampleRate: number;
  frames: TakeFrame[];
  recordedAt: number;
}): TakeJson => ({
  schema: 'muzly.take/1',
  segment: input.segment,
  recordedAt: input.recordedAt,
  pcm: {
    encoding: 'pcm_s16le',
    channels: 1,
    sampleRate: input.take.sampleRate,
    requestedSampleRate: input.requestedSampleRate,
    sampleCount: input.take.samples.length,
    durationSec: input.take.durationSec,
    trimmed: input.take.trimmed,
    base64: toBase64(new Uint8Array(toInt16(input.take.samples).buffer)),
  },
  frames: input.frames,
});
